import type { ReactNode } from 'react';
import { Spinner } from './Spinner';
import { EmptyBox, ErrorBox } from './ErrorBox';

export interface Column<T> {
  /** Khoa React cho cot — khong lien quan toi truong du lieu. */
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Class rieng cho o du lieu, vi du "text-right" hay "w-24". */
  className?: string;
  headerClassName?: string;
}

interface TableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T) => string | number;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  emptyHint?: string;
  /** Danh dau dong dang mo o khung chi tiet. */
  isActive?: (row: T) => boolean;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  error = null,
  onRetry,
  onRowClick,
  emptyMessage = 'Chưa có dữ liệu',
  emptyHint,
  isActive,
}: TableProps<T>) {
  if (error !== null) {
    return <ErrorBox message={error} onRetry={onRetry} />;
  }

  // Lan tai dau tien: hien spinner. Cac lan sau giu bang cu de man hinh khong nhay.
  if (loading && rows.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <Spinner size={22} label="Đang tải" />
      </div>
    );
  }

  if (rows.length === 0) {
    return <EmptyBox message={emptyMessage} hint={emptyHint} />;
  }

  // Giu tham chieu nguyen ban de TypeScript thu hep kieu duoc ngay tai cho dung.
  const clickable = onRowClick !== undefined;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-full border-collapse">
        <thead className="border-b border-slate-200 dark:border-slate-800">
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col" className={`cf-th ${col.headerClassName ?? ''}`}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((row) => {
            const active = isActive !== undefined && isActive(row);
            return (
              <tr
                key={rowKey(row)}
                className={[
                  active ? 'bg-sky-50 dark:bg-sky-950/50' : '',
                  clickable
                    ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60'
                    : '',
                ].join(' ')}
                onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={
                  onRowClick === undefined
                    ? undefined
                    : (event) => {
                        // Dong bang bam duoc bang ban phim — day la man hinh dung nhieu.
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                }
              >
                {columns.map((col) => (
                  <td key={col.key} className={`cf-td ${col.className ?? ''}`}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {loading && (
        <div className="flex justify-center p-3">
          <Spinner label="Đang tải thêm" />
        </div>
      )}
    </div>
  );
}
